import { isDesktop } from 'react-device-detect';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useRef, useCallback } from 'react';

import { Store } from '../../state/store.interface';
import Icon from '../../common/components/Icon/Icon';
import { roundWeight, getWeightUnit } from '../../config/commons';
import FullScreenFrame from '../../common/components/FullScreen/FullScreen';
import MobileFloatingButton from '../../common/components/FloatingButton/MobileFloatingButton';

import Legend from './Legend';
import styles from './Sample.module.scss';
import { Percentile } from './interfaces';
import { getSampleTitle } from './helpers';
import * as sampleSlice from './sampleSlice';
import QuantityAnimals from './QuantityAnimals';
import { getAreaPercentIncrease } from './helpers';
import SampleD3, { SampleD3Props } from './SampleD3';
import { groupLabels, createListPercentiles, createListPercentilesOld } from './math-percentile';

let sampleChartFullScreen: SampleD3 | null;

interface Props {
  image: HTMLImageElement;
}

export const SampleFullScreen = (props: Props) => {
  const { image } = props;

  const dispatch = useDispatch();

  const { allAnalysis, analysis: analysisOriginal, currentIndex, theme } = useSelector((state: Store) => state.detail);
  const {
    showAnimalsRemoved,
    percentileList,
    showQuantityAnimalsPanel,
    showModal,
  } = useSelector((state: Store) => state.sample);

  const currentAnalysis = (allAnalysis && allAnalysis.length > 0) ? allAnalysis[currentIndex] : analysisOriginal;
  const analysisType = currentAnalysis?.type || '';
  const analysisPhaseType = currentAnalysis.phaseType;
  const larvaeModifiedList = currentAnalysis.inferenceData.larvaeModifiedList;

  const refChartFullScreen = useRef<HTMLDivElement>(null);

  const averageWeight = currentAnalysis.resultData.averageWeight;
  const labelsUnit = getWeightUnit(averageWeight);

  const updatePercentiles = useCallback(() => {
    if (!currentAnalysis._id || !image || image?.height === 0) {
      return;
    }

    const { pointSizeRatio } = currentAnalysis;
    const { larvaeNumber } = currentAnalysis.resultData;
    const { weightGroups } = currentAnalysis.inferenceData;

    const labels = roundWeight({ value: currentAnalysis.resultData.labels, showUnit: false, average: averageWeight });
    const labelsSort: string[] = groupLabels(labels as number[]);

    const areaPercentIncrease = getAreaPercentIncrease({ analysisType }) * larvaeNumber;
    const percentiles: Percentile[] = pointSizeRatio === undefined || pointSizeRatio === null ?
      createListPercentilesOld({ labelsSort, areaPercentIncrease, weightGroups }) :
      createListPercentiles({ labelsSort, pointSizeRatio, weightGroups, imageSize: image.height });

    dispatch(sampleSlice.setPercentileList(percentiles));
  }, [currentAnalysis._id, averageWeight, analysisType, image?.height]);

  useEffect(() => {
    return (() => {
      sampleChartFullScreen = null;
    });
  }, []);

  useEffect(() => {
    updatePercentiles();
  }, [updatePercentiles]);

  useEffect(() => {
    if (!currentAnalysis?.imageData?.reduced?.url || !percentileList?.length || !showModal || !refChartFullScreen.current || !image) {
      return;
    }

    if (sampleChartFullScreen) {
      sampleChartFullScreen.refreshChart({
        animalsByGroup: percentileList,
        showAnimalsRemoved,
        currentIndex,
        urlImage: currentAnalysis.imageData.reduced.url,
      });

      return;
    }

    const propsFullScreen: SampleD3Props = {
      height: image.height,
      width: image.width,
      urlImage: currentAnalysis.imageData.reduced.url,
      animalsByGroup: percentileList,
      phaseType: analysisPhaseType,
      showAnimalsRemoved,
      container: refChartFullScreen.current,
      translateX: 0,
      translateY: 0,
      enableScroll: true,
      sightIconColor: 'black',
    };

    sampleChartFullScreen = new SampleD3(propsFullScreen);
  }, [refChartFullScreen.current, showModal, currentIndex, percentileList, image?.height, image?.width, currentAnalysis._id, currentAnalysis?.imageData?.reduced?.url, analysisPhaseType, showAnimalsRemoved, larvaeModifiedList?.length, image]);

  const changeListPercentile = (percentiles: Percentile[]) => {
    dispatch(sampleSlice.setPercentileList(percentiles));
  };

  const renderToolsPanel = () => {
    return (
      <QuantityAnimals
        changeListPercentile={changeListPercentile}
        labelsUnit={labelsUnit}
        listPercentile={percentileList as Percentile[]}
        showAnimalsRemoved={showAnimalsRemoved}
        showQuantityAnimalsPanel={showQuantityAnimalsPanel}
      />
    );
  };

  const renderImage = () => {
    const width = image?.width || 0;
    const show = !!width;

    return (
      <div className={styles.containerFullScreen}>
        <div className={styles.containerImageFullScreen}>
          <div
            ref={refChartFullScreen}
            style={{
              width: image?.width,
              height: show ? image?.height : 0,
              marginLeft: window.innerWidth - width > 0 ? (window.innerWidth - width) / 2 : 0,
            }}
          />
        </div>

        {renderToolsPanel()}
        <Legend analysisPhaseType={currentAnalysis.phaseType} />

        {
          !isDesktop &&
          <MobileFloatingButton
            className={styles.floatingButton}
            onClose={() => {
              dispatch(sampleSlice.setShowQuantityAnimalsPanel(true));
            }}
          >
            <Icon className={styles.filterIcon} name='filter' theme={theme} />
          </MobileFloatingButton>
        }
      </div>
    );
  };

  return (
    <FullScreenFrame
      show={showModal}
      title={getSampleTitle(currentAnalysis.code)}
      theme={theme}
      onClose={() => {
        dispatch(sampleSlice.setShowModal(false));
        dispatch(sampleSlice.setShowAnimalsRemoved(true));
        updatePercentiles();
      }}
    >
      {renderImage()}
    </FullScreenFrame>
  );
};
